import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, FolderPlusIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { projectsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const PROJECT_NAME_MAX_LENGTH = 80;

export default function CreateProjectModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [errors, setErrors] = useState({});

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Verbatim from 19.3 except the standard picker: the main app fetches a standards list to
  // populate a <select>, but that endpoint (GET /api/v1/standards) is P4 work and does not
  // exist yet. So standard_id is omitted from the payload entirely - the server then assigns
  // GRIHA v6.0 itself (ProjectCreate.standard_id is optional, contract above) - and this
  // read-only field just shows what will happen. P4 turns this back into a live select.
  const createProjectMutation = useMutation(
    (payload) => projectsAPI.create(payload),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('projects');
        toast.success('Project created successfully!');
        handleClose();
        const projectId = response.data?.id;
        if (projectId) {
          navigate(`/projects/${projectId}`);
        }
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'Failed to create project'));
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = 'Project name is required';
    } else if (trimmedName.length > PROJECT_NAME_MAX_LENGTH) {
      newErrors.name = `Project name must be ${PROJECT_NAME_MAX_LENGTH} characters or fewer`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // No standard_id key at all (not even null) - that is the exit-check assertion this
    // component pins: the server-side default only fires when the field is absent.
    createProjectMutation.mutate({
      name: trimmedName,
      description: formData.description.trim() || null,
    });
  };

  const handleClose = () => {
    setFormData({ name: '', description: '' });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* QA 2026-09-29: not marked as a dialog, and the X had no name. */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-project-title"
          className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FolderPlusIcon className="h-6 w-6 text-blue-500 mr-2" />
              <h3 id="create-project-title" className="text-lg font-medium text-gray-900">
                Create New Project
              </h3>
            </div>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Project Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                maxLength={PROJECT_NAME_MAX_LENGTH}
                placeholder="Enter project name"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={createProjectMutation.isLoading}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter project description (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={createProjectMutation.isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certification Standard
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-[#F8F8FF] flex items-center text-sm text-gray-700">
                <ShieldCheckIcon className="h-4 w-4 mr-2 text-gray-400" />
                GRIHA v6.0
              </div>
              {/* P4 adds GET /api/v1/standards; this field becomes a live select once that
                  route exists. Until then every project is assigned GRIHA v6.0 by the
                  server (19.3's exit check), and this just shows that outcome. */}
              <p className="mt-1 text-xs text-gray-500">
                Every project uses GRIHA v6.0 for now. Choosing a standard arrives in a later phase.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                Projects help organize your videos, photos and compliance runs by certification
                standard. Everything uploaded to this project is analyzed against GRIHA v6.0.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-[#F8F8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={createProjectMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createProjectMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createProjectMutation.isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creating...
                  </div>
                ) : (
                  'Create Project'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
